/**
 * 69.58 step 2, the flares voiced (the rules are `countermeasureMix.ts`; the audition sound_sheets/aircraft_defence.html):
 * one-shots built from the gunfire's parts (gunfire.ts `voice`, `chain`, `burst`, `tone`: its shared noise, its voice cap).
 * The roof turret is the autocannon's voice (autocannonSound.ts `AutocannonVoice`) and needs nothing here.
 *   - **The pop** (a cartridge): `thump`, a dull thud and a low push; `crack`, a sharp crack over a little body.
 *   - **The hiss** (a flare burning): `thump`'s a roaring hiss, swelling in and dying away over its burn; `crack`'s a
 *     brighter fizz with a crackle through it.
 *   - **The airburst** (a missile on a flare, the sheet's second style): the LAW's burst's crack and roll without its ground
 *     thump, the roll longer.
 * Cost: a pop 3 nodes for 0.3 s; a salvo's hiss 2–3 bursts for its 2.5 s burn; an airburst 4 for 1.5 s. Presentation only.
 */
import { burst, chain, tone, voice, type Voice } from './gunfire';
import { duckMusic } from './sfx';
import { musicDuckFor, type Heard } from './gunfireMix';
import { COUNTERMEASURE_STYLE, FLARE_SALVO, type FlareStyle } from './countermeasureMix';

function popInto(v: Voice, style: FlareStyle): void {
  if (style === 'thump') {
    burst(v, { dur: 0.05, vol: 0.7, type: 'bandpass', freq: 900, Q: 0.6 });
    tone(v, { dur: 0.12, vol: 0.6, freq: 110, to: 50 });
    burst(v, { dur: 0.2, vol: 0.3, type: 'lowpass', freq: 400 });
  } else {
    burst(v, { dur: 0.02, vol: 0.8, type: 'bandpass', freq: 2600, Q: 0.7 });
    tone(v, { dur: 0.07, vol: 0.35, freq: 160, to: 70 });
    burst(v, { dur: 0.12, vol: 0.2, type: 'lowpass', freq: 600 });
  }
}
function hissInto(v: Voice, style: FlareStyle, seconds: number): void {
  if (style === 'thump') {
    burst(v, { dur: seconds, vol: 0.6, type: 'bandpass', freq: 1300, freqTo: 900, Q: 0.5, attack: 0.15 });
    burst(v, { dur: seconds * 0.8, vol: 0.25, type: 'lowpass', freq: 350, attack: 0.2 });
  } else {
    burst(v, { dur: seconds, vol: 0.5, type: 'bandpass', freq: 3200, freqTo: 2400, Q: 0.7, attack: 0.08 });
    burst(v, { dur: seconds, vol: 0.18, type: 'highpass', freq: 5000, attack: 0.08 });
    burst(v, { dur: seconds * 0.7, vol: 0.15, type: 'lowpass', freq: 500, attack: 0.1 });
  }
}
function airburstInto(v: Voice, h: Heard): void {
  burst(v, { dur: 0.07, vol: 0.8, type: 'bandpass', freq: 3600, Q: 0.4 });
  burst(v, { dur: 0.5, vol: 0.55, type: 'lowpass', freq: 700, freqTo: 180 });
  burst(v, { at: 0.03, dur: 0.6, vol: 0.4, type: 'bandpass', freq: 1200, freqTo: 350, Q: 0.5 });
  burst(v, { at: 0.06, dur: Math.max(1, h.tail * 1.6), vol: 0.35, type: 'lowpass', freq: 600 });
}

/** A flare salvo's cartridges into the SFX bus: `pop`, heard as `countermeasureMix.hearFlare('pop', …)` gives. */
export function playFlarePops(h: Heard, style: FlareStyle = COUNTERMEASURE_STYLE.flare): void {
  const S = FLARE_SALVO[style];
  for (let i = 0; i < S.cartridges; i++) {
    const v = voice({ ...h, delay: h.delay + i * S.apart }, 0.3);
    if (!v) return;
    if (i === 0) duckMusic(musicDuckFor(h.gain * 0.5));
    popInto(v, style);
  }
}
/** The salvo's burning hiss, from where it was popped, for its burn. */
export function playFlareHiss(h: Heard, style: FlareStyle = COUNTERMEASURE_STYLE.flare): void {
  const seconds = FLARE_SALVO[style].burn, v = voice(h, seconds + 0.1);
  if (v) hissInto(v, style, seconds);
}
/** An airburst (a missile on a flare, the sheet's second style), heard as a blast is (gunfireMix `hearBlast`). */
export function playAirburst(h: Heard): void {
  const v = voice(h, Math.max(1.5, h.tail * 1.6 + 0.1));
  if (!v) return;
  duckMusic(musicDuckFor(h.gain));
  airburstInto(v, h);
}

/** The sound sheet's looks at them: rendered into `dest` of `ctx` from `at` (the context's seconds). */
export function renderFlarePops(ctx: BaseAudioContext, h: Heard, style: FlareStyle, at: number, dest: AudioNode = ctx.destination): void {
  const S = FLARE_SALVO[style];
  for (let i = 0; i < S.cartridges; i++) popInto(chain(ctx, dest, h, at + i * S.apart).v, style);
}
export function renderFlareHiss(ctx: BaseAudioContext, h: Heard, style: FlareStyle, at: number, dest: AudioNode = ctx.destination): void {
  hissInto(chain(ctx, dest, h, at).v, style, FLARE_SALVO[style].burn);
}
export function renderAirburst(ctx: BaseAudioContext, h: Heard, at: number, dest: AudioNode = ctx.destination): void {
  airburstInto(chain(ctx, dest, h, at).v, h);
}
